import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Settings2, Key, User, Calendar, Loader2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function ZohoSmartDashboardConfig({ form, onFormUpdated }) {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!form.primary_key_field);
  const fields = form?.detected_fields || [];

  if (fields.length === 0) return null;

  const handleChange = async (key, val) => {
    setSaving(true);
    try {
      const res = await zohoAPI.updateForm(form.id, { [key]: val === '_none' ? '' : val });
      onFormUpdated(res.data);
      toast.success('Configuration saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const configured = !!form.primary_key_field;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/30" data-testid="smart-config-trigger">
          <div className="flex items-center gap-2 text-sm">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Smart Dashboard Config</span>
            {configured ? (
              <span className="text-xs text-muted-foreground">
                Key: {form.primary_key_field} | Name: {form.display_name_field || '—'} | Period: {form.period_field || '—'}
              </span>
            ) : (
              <span className="text-xs text-amber-600">Not configured — set a primary key field to enable</span>
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border border-t-0 rounded-b-md p-3 space-y-3" data-testid="smart-config-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <Key className="h-3 w-3" />Primary Key *
              </Label>
              <Select value={form.primary_key_field || '_none'} onValueChange={v => handleChange('primary_key_field', v)} disabled={saving}>
                <SelectTrigger className="h-8 text-xs" data-testid="pk-field-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not set —</SelectItem>
                  {fields.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <User className="h-3 w-3" />Display Name
              </Label>
              <Select value={form.display_name_field || '_none'} onValueChange={v => handleChange('display_name_field', v)} disabled={saving}>
                <SelectTrigger className="h-8 text-xs" data-testid="name-field-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not set —</SelectItem>
                  {fields.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                <Calendar className="h-3 w-3" />Period Field
              </Label>
              <Select value={form.period_field || '_none'} onValueChange={v => handleChange('period_field', v)} disabled={saving}>
                <SelectTrigger className="h-8 text-xs" data-testid="period-field-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not set —</SelectItem>
                  {fields.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
